import {
  getPlatformCapability,
  PLATFORM_NAMES,
  type PlatformCapability,
  type PlatformCapabilitySupport,
  type PlatformName,
} from "@newform/constants";
import type { Platform } from "@newform-monorepo/db";
import type { ComponentType } from "react";
import {
  GoogleIcon,
  MetaIcon,
  MetaIconDark,
  SnapchatIcon,
  TikTokIcon,
  TiktokIconDark,
} from "@/components/Icons";
import { BrandIcon } from "@/components/ui/design-system/brand-icon";

/** Official mark, from packages/nf-design-system/icons/brand/applovin.svg. */
const AppLovinMark = ({ className }: { className?: string }) => (
  <BrandIcon name="applovin" className={className} />
);
/** On a brand-filled button the mark has to read against the fill. */
const AppLovinMarkMono = ({ className }: { className?: string }) => (
  <BrandIcon name="applovin" variant="mono" className={className} />
);

interface PlatformIconProps {
  className?: string;
}

export interface PlatformMetadata {
  readonly name: string;
  readonly adsName: string;
  readonly managerName: string;
  readonly icon: ComponentType<PlatformIconProps>;
  readonly connectIcon: ComponentType<PlatformIconProps>;
  readonly color: string;
  readonly hoverColor: string;
  readonly pill: string;
  readonly tint: string;
  readonly helpLink: string;
  readonly helpText: string;
  readonly idLabel: string;
  readonly idPlaceholder: string;
  readonly idSelectPlaceholder: string;
}

/**
 * Canonical frontend presentation metadata for every advertising platform.
 * Platform-specific feature implementations remain in their own modules;
 * shared labels, icons, colors, links, and controls derive from this registry.
 */
export const PLATFORM_METADATA = {
  META: {
    name: "Meta",
    adsName: "Meta Ads",
    managerName: "Meta Ads Manager",
    icon: MetaIconDark,
    connectIcon: MetaIcon,
    color: "bg-[#1877F2]",
    hoverColor: "hover:bg-[#0C63D4]",
    pill: "bg-[#0081FB]/10 text-[#0081FB]",
    tint: "from-[#0081FB]/10 via-[#0081FB]/4 to-[#0081FB]/0",
    helpLink: "https://www.facebook.com/business/help/1492627900875762",
    helpText: "Find your Meta Ad Account ID",
    idLabel: "Ad account ID",
    idPlaceholder: "Meta ad account ID",
    idSelectPlaceholder: "Select Meta ad account…",
  },
  TIKTOK: {
    name: "TikTok",
    adsName: "TikTok Ads",
    managerName: "TikTok Ads Manager",
    icon: TiktokIconDark,
    connectIcon: TikTokIcon,
    color: "bg-black",
    hoverColor: "hover:bg-gray-800",
    pill: "bg-nf-ds-gray-800/10 text-nf-ds-gray-800",
    tint: "from-[#FE2858]/10 via-[#FE2858]/4 to-[#FE2858]/0",
    helpLink:
      "https://ads.tiktok.com/help/article/ad-account-information-faq?lang=en",
    helpText: "Find your TikTok Advertiser ID",
    idLabel: "Advertiser ID",
    idPlaceholder: "TikTok advertiser ID",
    idSelectPlaceholder: "Select TikTok advertiser…",
  },
  GOOGLE: {
    name: "Google",
    adsName: "Google Ads",
    managerName: "Google Ads",
    icon: GoogleIcon,
    connectIcon: GoogleIcon,
    color: "bg-[#4285F4]",
    hoverColor: "hover:bg-[#3367D6]",
    pill: "bg-[#4285F4]/10 text-[#4285F4]",
    tint: "from-[#4285F4]/10 via-[#4285F4]/4 to-[#4285F4]/0",
    helpLink: "https://support.google.com/google-ads/answer/1704344",
    helpText: "Find your Google Ads IDs",
    idLabel: "Customer account ID",
    idPlaceholder: "Google Ads customer account ID (e.g., 123-456-7890)",
    idSelectPlaceholder: "Select Google Ads customer account…",
  },
  SNAPCHAT: {
    name: "Snapchat",
    adsName: "Snapchat Ads",
    managerName: "Snapchat Ads Manager",
    icon: SnapchatIcon,
    connectIcon: SnapchatIcon,
    color: "bg-yellow-300",
    hoverColor: "hover:bg-yellow-400",
    pill: "bg-yellow-400/15 text-yellow-800",
    tint: "from-[#FFFC00]/10 via-[#FFFC00]/4 to-[#FFFC00]/0",
    helpLink: "https://businesshelp.snapchat.com/s/article/ad-account-settings",
    helpText: "Find your Snapchat Ad Account ID",
    idLabel: "Ad account ID",
    idPlaceholder: "Snapchat ad account ID",
    idSelectPlaceholder: "Select Snapchat ad account…",
  },
  APPLOVIN: {
    name: "AppLovin",
    adsName: "AppLovin Ads",
    managerName: "AppLovin Dashboard",
    icon: AppLovinMark,
    connectIcon: AppLovinMarkMono,
    // Mirrors TikTok, the other near-black brand: DS gray tokens carry the
    // neutral treatment, and no brand hue is asserted because AppLovin's mark
    // is monochrome. The hex literals the other entries use are third-party
    // brand colors, which the design system deliberately does not define.
    color: "bg-black",
    hoverColor: "hover:bg-gray-800",
    pill: "bg-nf-ds-gray-800/10 text-nf-ds-gray-800",
    tint: "from-nf-ds-gray-800/10 via-nf-ds-gray-800/[0.04] to-transparent",
    helpLink:
      "https://support.applovin.com/en/growth/promoting-your-apps/api/reporting-api",
    helpText: "Find your AppLovin report key",
    idLabel: "Account ID",
    idPlaceholder: "AppLovin account ID",
    idSelectPlaceholder: "Select AppLovin account…",
  },
  // Keyed by the database platform set, not the active one: a platform can
  // exist in Prisma before any capability routes to it, and the surfaces below
  // still need a name and an icon to render a row for an account that already
  // exists. What gates availability is getPlatformOptions, which filters by
  // declared capability.
} as const satisfies Readonly<Record<Platform, PlatformMetadata>>;

export interface PlatformOption {
  readonly value: PlatformName;
  readonly label: string;
  readonly support: PlatformCapabilitySupport;
}

export function isPlatformName(value: string): value is PlatformName {
  return PLATFORM_NAMES.some((platform) => platform === value);
}

export function getPlatformDisplayName(platform: string): string {
  const normalized = platform.toUpperCase();
  return isPlatformName(normalized)
    ? PLATFORM_METADATA[normalized].name
    : platform;
}

export function getPlatformOptions(
  capability: PlatformCapability,
): PlatformOption[] {
  return PLATFORM_NAMES.flatMap((platform) => {
    const support = getPlatformCapability(platform, capability);
    return support.status === "supported"
      ? [{ value: platform, label: PLATFORM_METADATA[platform].name, support }]
      : [];
  });
}

export function PlatformIcon({
  platform,
  className,
  connect = false,
}: {
  platform: Platform;
  className?: string;
  connect?: boolean;
}) {
  const Icon = connect
    ? PLATFORM_METADATA[platform].connectIcon
    : PLATFORM_METADATA[platform].icon;
  return <Icon className={className} />;
}
